/* MiniSchedule — vertical timeline for today (8am-7pm), with hour ticks,
   "now" line, and events as colored bars. */

import { useNavigate } from 'react-router-dom';
import type { SyncedCalendarEvent } from '@/modules/calendar/types/calendar.types';

export interface MiniScheduleProps {
    events: SyncedCalendarEvent[];
    accent?: string;
    surface?: string;
    text?: string;
    muted?: string;
    rule?: string;
    eventBg?: string;
    eventBorder?: string;
}

const HOUR_START = 8;
const HOUR_END = 19;
const HOUR_HEIGHT = 28; // px per hour

function minutesFromStart(d: Date) {
    const h = d.getHours() + d.getMinutes() / 60;
    return (h - HOUR_START) * 60;
}

export function MiniSchedule({
    events,
    accent = '#fd775e',
    surface = 'rgba(255,255,255,0.65)',
    text = '#2a241e',
    muted = '#9c948a',
    rule = 'rgba(120, 90, 50, 0.08)',
    eventBg = 'rgba(253, 119, 94, 0.12)',
    eventBorder = '#fd775e',
}: MiniScheduleProps) {
    const navigate = useNavigate();
    const now = new Date();
    const totalMinutes = (HOUR_END - HOUR_START + 1) * 60;
    const containerHeight = (HOUR_END - HOUR_START + 1) * HOUR_HEIGHT;
    const nowOffset = (minutesFromStart(now) / totalMinutes) * containerHeight;
    const showNow = now.getHours() >= HOUR_START && now.getHours() < HOUR_END;

    return (
        <div
            style={{
                background: surface,
                borderRadius: 18,
                padding: 16,
                border: `1px solid ${rule}`,
                fontFamily: 'var(--v2-font-sans)',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: text }}>Schedule</span>
                <button
                    type="button"
                    onClick={() => navigate('/v2/calendar')}
                    style={{
                        fontSize: 10.5,
                        color: muted,
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        fontWeight: 600,
                    }}
                >
                    Open →
                </button>
            </div>
            <div
                style={{
                    position: 'relative',
                    display: 'grid',
                    gridTemplateColumns: '32px 1fr',
                    height: containerHeight,
                }}
            >
                <div style={{ position: 'relative' }}>
                    {Array.from({ length: HOUR_END - HOUR_START + 1 }).map((_, i) => {
                        const h = HOUR_START + i;
                        const label = h === 12 ? '12p' : h > 12 ? `${h - 12}p` : `${h}a`;
                        return (
                            <div
                                key={h}
                                style={{
                                    position: 'absolute',
                                    top: i * HOUR_HEIGHT - 6,
                                    fontSize: 9,
                                    color: muted,
                                    fontWeight: 600,
                                    letterSpacing: '0.05em',
                                    textTransform: 'uppercase',
                                    width: 28,
                                    textAlign: 'right',
                                }}
                            >
                                {label}
                            </div>
                        );
                    })}
                </div>
                <div style={{ position: 'relative', borderLeft: `1px solid ${rule}` }}>
                    {Array.from({ length: HOUR_END - HOUR_START + 1 }).map((_, i) => (
                        <div
                            key={i}
                            style={{
                                position: 'absolute',
                                top: i * HOUR_HEIGHT,
                                left: 0,
                                right: 0,
                                borderTop: i === 0 ? 'none' : `1px solid ${rule}`,
                            }}
                        />
                    ))}
                    {events.map((ev, idx) => {
                        const start = new Date(ev.startDateTime);
                        const end = new Date(ev.endDateTime);
                        const top = (minutesFromStart(start) / totalMinutes) * containerHeight;
                        const height = Math.max(
                            14,
                            ((end.getTime() - start.getTime()) / 60_000 / totalMinutes) * containerHeight,
                        );
                        if (top < 0 || top > containerHeight) return null;
                        const who =
                            ev.attendees?.[0]?.name?.split(' ')[0] ||
                            ev.attendees?.[0]?.email?.split('@')[0] ||
                            ev.title;
                        return (
                            <button
                                key={idx}
                                type="button"
                                onClick={() => navigate('/v2/calendar')}
                                style={{
                                    position: 'absolute',
                                    top,
                                    left: 6,
                                    right: 6,
                                    height,
                                    background: eventBg,
                                    borderLeft: `2px solid ${eventBorder}`,
                                    borderRadius: 4,
                                    padding: '3px 8px',
                                    cursor: 'pointer',
                                    border: 'none',
                                    borderLeftWidth: 2,
                                    borderLeftStyle: 'solid',
                                    borderLeftColor: eventBorder,
                                    textAlign: 'left',
                                    color: text,
                                    overflow: 'hidden',
                                    fontSize: 11,
                                    fontWeight: 600,
                                    lineHeight: 1.2,
                                }}
                            >
                                {who}
                                <div style={{ fontSize: 10, color: muted, fontWeight: 500, marginTop: 1 }}>
                                    {start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                                </div>
                            </button>
                        );
                    })}
                    {showNow && (
                        <div
                            style={{
                                position: 'absolute',
                                top: nowOffset,
                                left: 0,
                                right: 0,
                                height: 0,
                                borderTop: `1.5px solid ${accent}`,
                                pointerEvents: 'none',
                            }}
                        >
                            <span
                                style={{
                                    position: 'absolute',
                                    left: -3,
                                    top: -3.5,
                                    width: 7,
                                    height: 7,
                                    borderRadius: '50%',
                                    background: accent,
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
